import { computed, observable, action } from 'mobx';
import isObject from 'lodash/isObject';
import { BaseBlock } from '~/models/blocks/base-block.model';
import {
    IContractDropdownField,
    IContractHTMLBlockResponse
} from '~/types/server.types';
import type { RootStore } from '~/stores/root.store';
import { IContract, ISmartField } from '~/types/model.types';
import { ContractBlockErrors } from '~/types/error.types';
import { arrayToHashMapById } from '~/utils/server-response.utils';
import type { FlowRevision } from '~/models/flow-revision.model';
import { IUpdateBlockContext } from '~/types/builder.types';
import { LogEvents } from '~/services/fullstory/log-events';
import type { ContractBlock } from '~/models/blocks/contract-block.model';
import { SmartFieldPermissions } from '~/components/Builder/BuilderSidebar/ContractSidebar/SmartFieldSidebar/SmartField.types';

export type IContractHTMLBlock = {
    html_body: string;
};

export type ContractHtmlBlockDataForServer = ReturnType<
    BaseBlock['getDataForServer']
> &
    Pick<IContractHTMLBlockResponse, 'contract' | 'type'>;

export class ContractHTMLBlock extends BaseBlock {
    type: 'contract_html_block';

    @observable contract: IContract;

    constructor(
        block: IContractHTMLBlockResponse,
        flowRevision: FlowRevision<RootStore>,
        rootStore: RootStore
    ) {
        super(block, flowRevision, rootStore);
        this.type = block.type;
        this.contract = block.contract;
    }

    @action
    onBlockCreated() {
        const {
            blockCtrl,
            builderUiStore: { selectedFlow }
        } = this.rootStore;
        blockCtrl.getContractFields(selectedFlow._id).then(() => {
            this.populateContractSmartFieldsValues();
        });
    }

    @computed
    get errors() {
        const {
            builderUiStore: { showErrors, selectedFlow }
        } = this.rootStore;

        if (selectedFlow.is_template || !showErrors) {
            return {};
        }

        const errors: any = {};
        if (this.blockInputValidations[ContractBlockErrors.VendorMustFill]) {
            errors.hasErrors = true;
        }
        return errors;
    }

    @action
    populateContractSmartFieldsValues() {
        this.contract.fields_metadata.forEach(fieldMetaData => {
            const isManualEdited =
                this.flowRevision.variablesMap[
                    fieldMetaData.connected_variable_id
                ];
            const smartFieldFromServer =
                this.findMatchingOptionalField(fieldMetaData);

            // We should accept an empty string as a value if the user manually changes it.
            // If the value has not changed by the user, an empty string is not valid.
            fieldMetaData.default_value = isManualEdited
                ? fieldMetaData.default_value ??
                  (smartFieldFromServer?.value || '')
                : fieldMetaData.default_value ||
                  smartFieldFromServer?.value ||
                  '';
        });
    }

    get isDeletable() {
        return false;
    }

    @computed
    get isContractBeforeServiceSelection() {
        return (this.parent as ContractBlock).isBeforeSelection;
    }

    @computed
    get isContractBeforeSchedulerBlock() {
        return (this.parent as ContractBlock).isBeforeSchedulerblock;
    }

    @computed
    get isServicesRecapDisabled() {
        const {
            builderUiStore: { selectedFlow }
        } = this.rootStore;
        return (
            (!selectedFlow.hasInvoiceBlock &&
                !selectedFlow.hasServiceSelectionBlocks) ||
            this.isContractBeforeServiceSelection
        );
    }
    @computed
    get isPaymentsRecapDisabled() {
        const {
            builderUiStore: { selectedFlow }
        } = this.rootStore;

        return (
            !selectedFlow.hasInvoiceBlock ||
            this.isContractBeforeServiceSelection
        );
    }

    shouldRequireFieldCompletion(smartField: ISmartField) {
        const isVendorRequiredEmptyField =
            smartField.fill_type === SmartFieldPermissions.CANT_EDIT &&
            !smartField.default_value &&
            !this.variablesValues[smartField.connected_variable_id];

        if (!isVendorRequiredEmptyField) {
            return false;
        }

        const scheduleContainerBlock = this.flow.scheduleContainerBlock;
        if (!scheduleContainerBlock) {
            return true;
        }

        const isSessionSource = smartField.data_source_type === 'session';
        const isProjectDateSessionDateAndIsProjectDateSource =
            smartField.data_source_type === 'project' &&
            smartField.data_source_property === 'event_date' &&
            scheduleContainerBlock.isProjectDateSessionDate;

        if (isSessionSource || isProjectDateSessionDateAndIsProjectDateSource) {
            const isClientMustSchedule = scheduleContainerBlock.is_required;
            const isClientMustPay = scheduleContainerBlock.isRequireToPay;
            return (
                this.isContractBeforeSchedulerBlock ||
                isClientMustPay ||
                !isClientMustSchedule
            );
        }
        return true;
    }

    @computed
    get blockInputValidations() {
        const validations = {};
        const vendorMustFill = this.contract.fields_metadata.filter(
            smartField => {
                return this.shouldRequireFieldCompletion(smartField);
            }
        ).length;

        const clientMustFill = this.contract.fields_metadata.filter(
            smartField => {
                return smartField.fill_type === 'client_required';
            }
        ).length;

        if (vendorMustFill) {
            validations[ContractBlockErrors.VendorMustFill] = vendorMustFill;
        }

        if (clientMustFill) {
            validations[ContractBlockErrors.ClientMustFill] = clientMustFill;
        }

        return validations;
    }

    @computed
    get fieldsMap(): Record<string, ISmartField> {
        const result = {};
        this.contract.fields_metadata.forEach((field: ISmartField) => {
            result[field.field_id] = field;
        });

        return result;
    }

    @computed
    get fields() {
        return this.contract.fields_metadata.filter(
            field => field.type === 'field'
        );
    }

    @computed
    get recaps() {
        return this.contract.fields_metadata.filter(
            field =>
                field.type === 'payments_recap' ||
                field.type === 'services_recap'
        );
    }

    @action
    mergeBlockAttributes(
        block: ContractHTMLBlock,
        attributes: Record<string, unknown>
    ) {
        const { fullstoryService } = this.rootStore;
        super.mergeBlockAttributes(block, attributes);

        if ('connected_variable_id' in attributes) {
            const fieldsById = arrayToHashMapById(
                block.contract.fields_metadata,
                'field_id'
            );
            this.contract.fields_metadata.forEach(field => {
                const responseField = fieldsById[field.field_id];

                if (!responseField && this.isFieldAlteredInFuture()) {
                    // If this field is changed in the future which means or created or deleted that means that there is a race condition of updating the fields metadata
                    // While a new field just got created or old field deleted and it's action is waiting to be resolved, in this case we want to skip this field
                    // And it would be updated in the result of the next action.
                    fullstoryService.warn(
                        LogEvents.FieldMetadataRaceConditionFound(
                            field.field_id,
                            block.block_id
                        )
                    );

                    return;
                }

                if (
                    field.connected_variable_id !==
                    responseField.connected_variable_id
                ) {
                    field.connected_variable_id =
                        responseField.connected_variable_id;
                }
            });
        }
    }

    @computed
    get variablesValues() {
        const values = {};
        this.contract.fields_metadata.forEach(data => {
            const { connected_variable_id, data_source_sub_property } = data;
            const variable =
                this.flowRevision.variablesMap[connected_variable_id];
            const variableValue = variable?.value ?? undefined;

            const value =
                data_source_sub_property && isObject(variableValue)
                    ? variableValue && variableValue[data_source_sub_property]
                    : variableValue;

            values[connected_variable_id] =
                data.type === 'field' ? this.formatValue(value) : value;
        });

        return values;
    }

    @computed
    get optionalFields() {
        return this.rootStore.builderUiStore.optionalFields;
    }

    isManualEdited(variableId: string) {
        return this.flowRevision.variablesMap[variableId]?.is_manual_value;
    }

    isDraggable() {
        return false;
    }

    dndAcceptedTypes() {
        return null;
    }

    @computed
    get shouldExpandOnSelect() {
        return false;
    }

    getDataForServer(): ContractHtmlBlockDataForServer {
        return {
            ...super.getDataForServer(),
            contract: { ...this.contract },
            type: 'contract_html_block'
        };
    }

    _findMatchingField<T extends ISmartField | IContractDropdownField>(
        smartField: Partial<ISmartField>,
        fields: T[]
    ) {
        return fields.find(
            field =>
                field.data_source_type === smartField.data_source_type &&
                field.data_source_property ===
                    smartField.data_source_property &&
                ((!field.data_source_sub_property &&
                    !smartField.data_source_sub_property) ||
                    field.data_source_sub_property?.toString() ===
                        smartField.data_source_sub_property)
        );
    }

    findMatchingOptionalField(smartField: Partial<ISmartField>) {
        return this._findMatchingField(smartField, this.optionalFields);
    }

    findMatchingSmartField(smartField: Partial<ISmartField>) {
        return this._findMatchingField(smartField, this.fields);
    }

    hasSmartFieldsWithSourceType(source_type: string) {
        return this.fields.some(
            field => field.data_source_type === source_type
        );
    }

    private formatValue(value: any) {
        if (Array.isArray(value)) {
            return value.join(', ');
        }

        return value;
    }

    private isFieldAlteredInFuture() {
        const {
            actionsManager: { actionsStack }
        } = this.rootStore;

        const waitingActions = actionsStack.getWaitingActions();

        return waitingActions.some(waitingAction =>
            (waitingAction.ctx as IUpdateBlockContext).commands.some(
                command =>
                    (command.data as ContractHTMLBlock)?.block_id ===
                    this.block_id
            )
        );
    }
}
